import { useEffect, useState } from "react";
import { SPHERE_SCALE_V82 } from "@/shared/config/pointsRules";

/**
 * Четыре счёта из пяти, которые считаются не здесь — мы только читаем.
 * Пятый, вклад, живёт в contribScore.ts.
 *
 * ВСЕ ЧЕТЫРЕ БЕРУТСЯ ОДНИМ ЗАПРОСОМ У ВОРКЕРА — `GET /account/scores`. Тем же,
 * которым живут шары в приложении.
 *
 * Раньше тренировки читались отдельно, у site_server
 * (`/api/user-stats/detailed-analysis`), и это давало РАЗНЫЕ ЧИСЛА в кабинете
 * и в телефоне под одной подписью. Проверено 05.09.2026 на одной учётной
 * записи в одну минуту: кабинет показывал 10 369,58 по 62 тренировкам, а
 * приложение — 10 245 по 68. Причина в источниках: detailed-analysis считает
 * по кешу тренировок, а воркер складывает all_scores.json, и кеш не видит
 * оценок, посчитанных телефоном.
 *
 * Спорить, какое из чисел вернее, здесь нечем и не нужно: под одной подписью
 * должно стоять одно число, и правильное место договориться о нём — воркер,
 * а не два клиента порознь.
 *
 * Ходят по токену воркера (Bearer), а не по подписанному токену сайта,
 * которым живёт вклад: это разные системы, и сводить их к одному ключу здесь
 * не место.
 */
const WORKER = "https://bucket.torsunovoleggen.workers.dev";

export type OtherScores = {
    workouts: number | null;
    workoutsCount: number | null;
    social: number | null;
    socialEntries: number | null;
    /**
     * Баллы за приведённых: по 2000 за каждую покупку по партнёрской ссылке.
     *
     * Именно баллы, а не головы. Шар наполняется до ста тысяч, как и три
     * остальных, и головами он не наполнился бы никогда — привести сто тысяч
     * человек не может никто. Само число людей лежит рядом, в referralsJoined:
     * его и показываем подписью.
     */
    referrals: number | null;
    /** Сколько человек купило по ссылке — то, что стоит за баллами. */
    referralsJoined: number | null;
    /**
     * Баллы за обучение: уроки курсов, напоминания после глав, просмотренные
     * лекции и вопросы специалистам. Считает их site_server, но сюда они
     * приходят от воркера вместе с остальными — чтобы под всеми пятью шарами
     * стояли числа из одного ответа, а не из пяти разных.
     */
    learning: number | null;
    /** Пройдено уроков курсов — подпись под шаром обучения. */
    learningLessons: number | null;
    /** Пройдено напоминаний после глав — второе число той же подписи. */
    learningQuizzes: number | null;
    loaded: boolean;
};

const EMPTY: OtherScores = {
    workouts: null,
    workoutsCount: null,
    social: null,
    socialEntries: null,
    referrals: null,
    referralsJoined: null,
    learning: null,
    learningLessons: null,
    learningQuizzes: null,
    loaded: false,
};

let cache: OtherScores | null = null;
let inflight: Promise<OtherScores> | null = null;
/**
 * Номер поколения: ответ запроса, начатого до сброса кеша, не должен затирать
 * уже полученный свежий.
 */
let generation = 0;
const listeners = new Set<(s: OtherScores) => void>();

const r2Token = () => (localStorage.getItem("personal_data_session_token") ||
        localStorage.getItem("auth_token") ||
        localStorage.getItem("r2_session_token") ||
        "");

/** Чей это счёт: токен воркера, по которому он прочитан (тот, что сейчас в localStorage). */
export const otherScoresToken = (): string => {
    try {
        return r2Token();
    } catch {
        return "";
    }
};

/**
 * Чей счёт в кеше (токен воркера). Выход и вход другого человека страницу не
 * перезагружают (UserStore.logout) — без этого он видел бы числа прежнего.
 */
let cacheToken = "";
let inflightToken = "";

/** Кеш — только если он того же человека, что вошёл сейчас. */
const cachedFor = (token: string): OtherScores | null => (cache && cacheToken === token ? cache : null);

export const fetchOtherScores = (): Promise<OtherScores> => {
    const token = r2Token();
    const hit = cachedFor(token);
    if (hit) return Promise.resolve(hit);
    if (inflight && inflightToken === token) return inflight;
    if (inflight || cache) {
        // Другой человек: ответ, начатый для прежнего, кеш не займёт.
        generation += 1;
        cache = null;
    }
    const gen = generation;
    inflightToken = token;
    const p: Promise<OtherScores> = (async () => {
        const email = localStorage.getItem("site_user_email") || "";
        // Токен воркера. Им ходят и мероприятия, и приглашённые — вопреки
        // тому, что было написано здесь раньше: строкой ниже этот же Bearer
        // уходит на /affiliate/arrivals. По подписанному токену сайта живёт
        // только вклад, и он считается в другом файле.
        const auth = { Authorization: `Bearer ${token}` };

        // Один запрос вместо трёх: воркер уже сложил всё, что нужно шарам.
        const all = await (async () => {
            try {
                const r = await fetch(`${WORKER}/account/scores`, { headers: auth });
                if (!r.ok) return null;
                const d = await r.json();
                return d?.success ? d : null;
            } catch {
                return null;
            }
        })();

        // Разбор обучения воркер отдаёт отдельным объектом и всегда целиком:
        // до первого начисления там шесть нулей, а не отсутствующее поле.
        // Регистр camelCase — соглашение ИМЕННО ЭТОГО ответа (workoutsCount,
        // referralsJoined рядом); леджер site_server и `/api/learning/me`
        // пишут те же величины в snake_case, см. learningScore.ts.
        const learn = (all?.learningDetail || {}) as {
            lessons?: number;
            quizzes?: number;
        };

        const next: OtherScores = {
            workouts: all ? Math.round(all.workouts || 0) : null,
            workoutsCount: all ? all.workoutsCount ?? null : null,
            social: all ? Math.round(all.social || 0) : null,
            socialEntries: all ? all.socialEntries ?? null : null,
            // Шар живёт в баллах, а не в головах: до ста тысяч головами его не
            // наполнить никому. Число людей лежит рядом и идёт в подпись.
            referrals: all ? Math.round(all.referrals || 0) : null,
            referralsJoined: all ? all.referralsJoined ?? 0 : null,
            learning: all ? Math.round(all.learning || 0) : null,
            learningLessons: all ? Math.round(learn.lessons || 0) : null,
            learningQuizzes: all ? Math.round(learn.quizzes || 0) : null,
            loaded: true,
        };
        if (gen === generation) {
            cache = next;
            cacheToken = token;
        }
        return next;
    })().finally(() => {
        if (inflight === p) inflight = null;
    });
    inflight = p;
    return p;
};

/**
 * Перечитать счета и раздать их подписчикам.
 *
 * До появления обучения кеш этого модуля не сбрасывало НИЧЕГО, кроме
 * перезагрузки страницы, и это было терпимо: тренировки, встречи и покупки по
 * ссылке засчитываются не на этом экране и не в эту минуту. Балл за пройденный
 * урок — засчитывается: человек нажимает «пройдено» и тут же смотрит на шар.
 */
export const refreshOtherScores = async (): Promise<OtherScores> => {
    generation += 1;
    cache = null;
    inflight = null;
    const next = await fetchOtherScores();
    listeners.forEach((l) => l(next));
    return next;
};

// Баллы за обучение начисляет site_server, а в шар они приходят от воркера —
// два разных пути к одному числу. Событие связывает их: начислили урок —
// перечитали общий ответ, и шар наливается без перезагрузки.
if (typeof window !== "undefined") {
    window.addEventListener("learning-updated", () => {
        void refreshOtherScores();
    });
}

export const useOtherScores = (): OtherScores => {
    const [v, setV] = useState<OtherScores>(() => cachedFor(r2Token()) ?? EMPTY);
    useEffect(() => {
        let alive = true;
        fetchOtherScores().then((s) => {
            if (alive) setV(s);
        });
        const onChange = (s: OtherScores) => setV(s);
        listeners.add(onChange);
        return () => {
            alive = false;
            listeners.delete(onChange);
        };
    }, []);
    return v;
};

/**
 * При каком счёте сфера полна. Задано владельцем: тренировки — 300000,
 * общественное — 200000, вклад, приглашённые и обучение — по 100000.
 *
 * Раньше наполнение шло по ступеням, и сфера успевала налиться на первой сотне
 * баллов — то есть говорила «почти всё», когда пройдена сотая часть пути.
 * Теперь это прямая доля от порога, и полная сфера значит ровно то, что
 * написано: счёт добран до конца.
 *
 * Цена честности — что поначалу заполнение почти не видно: 2000 баллов от
 * 300000 это меньше процента. Так и есть, и рисовать больше, чем набрано,
 * значило бы обманывать.
 *
 * ⚠ ЭТИ ЧИСЛА ЛЕЖАТ В ЧЕТЫРЁХ МЕСТАХ, и здесь только одно из них. Меняете
 * порог — меняйте везде, иначе один и тот же счёт нальёт шар по-разному на
 * сайте и в телефоне:
 *  1. здесь — сайт;
 *  2. `fullAt` в воркере Cloudflare (ответ `GET /account/scores`);
 *  3. `SCORES_FULL_AT` в `workers/yandex/community_social.py` — воркер RU;
 *  4. значения по умолчанию в DTO обеих сборок Android (`AccountScoresApi.kt`).
 *
 * Таблица баллов v8.2 (владелец, 29.09.2026) задаёт новые шкалы: вклад и приглашённые —
 * по 300 000, общественное — 120 000, тренировки — 100 000, обучение — 90 000
 * (pointsRules.SPHERE_SCALE_V82). Они рассчитаны на баллы v8.2, которые пока за выключенными
 * флагами, поэтому шары рисуются по сегодняшним шкалам, пока не включён
 * SPHERE_SCALES_V82_ENABLED. Включать вместе с флагами баллов и во всех четырёх местах
 * (в воркере — POINTS_V82_SCALES, `fullAt`), иначе сайт и телефон нальют шар по-разному.
 */
export const SPHERE_SCALES_V82_ENABLED = false;
export const SPHERE_FULL_AT_TODAY: Record<string, number> = {
    contrib: 100000,
    workouts: 300000,
    social: 200000,
    referrals: 100000,
    learning: 100000,
};
export const SPHERE_FULL_AT: Record<string, number> = SPHERE_SCALES_V82_ENABLED
    ? { ...SPHERE_SCALE_V82 }
    : SPHERE_FULL_AT_TODAY;

/** Доля до полной сферы: 0 — пусто, 1 — набрано всё. */
export const sphereFill = (value: number, key: string): number => {
    const target = SPHERE_FULL_AT[key] || 0;
    if (!target || value <= 0) return 0;
    return Math.min(1, value / target);
};

